import * as d3 from "d3";
import _ from "lodash";
import moment from "moment";
import datetimeManager from "../managers/datetime";
import mathManager from "../managers/math";
import commonRenderer from "../renderers/common";
import {
  CHART_LAYER_MID_PRICE,
  CHART_LAYER_LEVELS_TOP,
  CHART_LAYER_LEVELS_TOP_3,
  CHART_LAYER_LEVELS_TOP_5,
  CHART_LAYER_LEVELS_TOP_10,
  API_KEYS_BID_ASK_QUANTITY
} from "../managers/constants";
import { CHART_LABEL_TIME, CHART_LABEL_QUANTITY, CHART_LABEL_MID_PRICE } from "../managers/string-literals";

// Note: This file is still under progress and therefore please do not review it yet

const service = (() => {
  const render = (params) => {
    // View / hide layers on the drawing
    let showMidPriceLineLayer = _.includes(params.selectedLayers, CHART_LAYER_MID_PRICE) ? true : false;
    let showTopOfTheBookLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP) ? true : false;
    let showTop3LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_3) ? true : false;
    let showTop5LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_5) ? true : false;
    let showTop10LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_10) ? true : false;
    let howManyLayers = 0;
    if (showTopOfTheBookLayer) howManyLayers = 1;
    if (showTop3LevelsLayer) howManyLayers = 3;
    if (showTop5LevelsLayer) howManyLayers = 5;
    if (showTop10LevelsLayer) howManyLayers = 10;

    // Dimensions
    let margin = params.dimensions.margin,
      width = params.dimensions.width,
      height = params.dimensions.height;

    let lineCurveAlpha = 0.5; // Curvature of lines

    // Positioning for tick labels on axis
    let xAxisLabel = CHART_LABEL_TIME;
    let yAxisLabel = CHART_LABEL_QUANTITY;
    let dualYAxisLabel = CHART_LABEL_MID_PRICE;

    // Unique class for drag movements across X-axis
    let uniqueLineClass = "line" + params.uniqueKey;

    // Positioning for labels on axis
    let lineStrokeWidth = 1;

    //To show the scale of only visible layers.
    let yAxisScaleRangeProperties = [];
    if (showTopOfTheBookLayer) {
      yAxisScaleRangeProperties = [API_KEYS_BID_ASK_QUANTITY.l1Ask, API_KEYS_BID_ASK_QUANTITY.l1Bid];
    } else if (showTop3LevelsLayer) {
      yAxisScaleRangeProperties = [
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Bid
      ];
    } else if (showTop5LevelsLayer) {
      yAxisScaleRangeProperties = [
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Bid,
        API_KEYS_BID_ASK_QUANTITY.l4Ask,
        API_KEYS_BID_ASK_QUANTITY.l4Bid,
        API_KEYS_BID_ASK_QUANTITY.l5Ask,
        API_KEYS_BID_ASK_QUANTITY.l5Bid
      ];
    } else if (showTop10LevelsLayer) {
      yAxisScaleRangeProperties = [
        API_KEYS_BID_ASK_QUANTITY.l1Ask,
        API_KEYS_BID_ASK_QUANTITY.l1Bid,
        API_KEYS_BID_ASK_QUANTITY.l2Ask,
        API_KEYS_BID_ASK_QUANTITY.l2Bid,
        API_KEYS_BID_ASK_QUANTITY.l3Ask,
        API_KEYS_BID_ASK_QUANTITY.l3Bid,
        API_KEYS_BID_ASK_QUANTITY.l4Ask,
        API_KEYS_BID_ASK_QUANTITY.l4Bid,
        API_KEYS_BID_ASK_QUANTITY.l5Ask,
        API_KEYS_BID_ASK_QUANTITY.l5Bid,
        API_KEYS_BID_ASK_QUANTITY.l6Ask,
        API_KEYS_BID_ASK_QUANTITY.l6Bid,
        API_KEYS_BID_ASK_QUANTITY.l7Ask,
        API_KEYS_BID_ASK_QUANTITY.l7Bid,
        API_KEYS_BID_ASK_QUANTITY.l8Ask,
        API_KEYS_BID_ASK_QUANTITY.l8Bid,
        API_KEYS_BID_ASK_QUANTITY.l9Ask,
        API_KEYS_BID_ASK_QUANTITY.l9Bid,
        API_KEYS_BID_ASK_QUANTITY.l10Ask,
        API_KEYS_BID_ASK_QUANTITY.l10Bid
      ];
    }
    // ==================================================
    // CREATE SVG
    // ==================================================

    let svg = commonRenderer.createSvg(params.drawingContainerElementId, width, height, margin);

    // ==================================================
    // CREATE SCALE
    // ==================================================

    // Time scale for X axis
    let x = d3
      .scaleTime()
      .range([1, width])
      .domain(
        d3.extent(params.data, function(d) {
          return datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset);
        })
      );

    // Linear scale for Y axis
    let y = d3
      .scaleLinear()
      .range([height, 1])
      .domain(commonRenderer.recalculateDomainForMirroring(commonRenderer.getMinMaxValues(params.data, yAxisScaleRangeProperties)))
      .nice();

    // Linear scale for Y-Dual axis
    let dualY = d3
      .scaleLinear()
      .range([height, 1])
      .domain(commonRenderer.getMinMaxValues(params.data, API_KEYS_BID_ASK_QUANTITY.midPrice))
      .nice();

    // ==================================================
    // FIND MIN & MAX VALUES
    // ==================================================
    // For X axis
    var xDomain = d3.extent(params.data, function(d) {
      return datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset);
    });

    // ==================================================
    // CREATE AXES
    // ==================================================

    // Create X axis
    let xAxis = commonRenderer.createXAxisTimeScale(
      x,
      commonRenderer.generateXAxisTickValues(params.data, params.zoneOffset, params.granularityCode, width),
      params.tickFormat
    );

    // Create Y axis
    let yAxis = commonRenderer.createYAxisLinearScale(y);

    // Create Y-Dual axis
    let dualYAxis = commonRenderer.createDualYAxisLinearScale(dualY);

    // ==================================================
    // DRAW AXES
    // ==================================================
    // Draw X axis

    commonRenderer.drawXAxis(svg, height, xAxis);

    // Draw Y axis
    commonRenderer.drawYAxis(svg, yAxis);

    if (showMidPriceLineLayer) {
      // Draw Y-Dual axis
      commonRenderer.drawDualYAxis(svg, width, dualYAxis);
    }

    // ==================================================
    // DRAW LABEL on AXES
    // ==================================================

    // Label for X axis
    commonRenderer.drawXAxisLabel(svg, width, height, xAxisLabel);

    // Label for Y axis
    commonRenderer.drawYAxisLabel(svg, height, yAxisLabel, margin);

    if (showMidPriceLineLayer) {
      // Label for Y-Dual axis
      commonRenderer.drawYDualAxisLabel(svg, width, height, dualYAxisLabel, margin);
    }

    // ==================================================
    // CREATE COMMON TOOLTIP
    // ==================================================
    //common tooltip
    let tooltip = commonRenderer.drawChartDataTooltip(params.drawingContainerElementId, params.uniqueKey);

    // ==================================================
    // CREATE TOOLTIP FOR TOP OF THE BOOK ASK FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    let yAxisAskTooltip = commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, "yAxisAskTooltip", margin, width);

    // ==================================================
    // CREATE TOOLTIP FOR TOP OF THE BOOK BID FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    let yAxisBidTooltip = commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, "yAxisBidTooltip", margin, width);

    // ==================================================
    // HIGHLIGH ROLE SHIFT, ADD ALTERNATE BACKGROUND COLORS.
    // ==================================================
    commonRenderer.highlightRoleShift(params.data, params.zoneOffset, API_KEYS_BID_ASK_QUANTITY, svg, height, x);

    // ==================================================
    //  DRAWING BID/ASK AREA SHADES
    // ==================================================
    let getArea = function(data) {
      return d3
        .area()
        .x(function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset));
        })
        .y0(function() {
          return y(0);
        })
        .y1(data)
        .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));
    };

    //Drawing last layer first.
    for (let i = howManyLayers; i >= 1; i--) {
      //Drawing area shades.
      svg
        .append("path")
        .datum(
          params.data.filter(function(obj) {
            return obj[API_KEYS_BID_ASK_QUANTITY["l" + i + "Bid"]] ? true : false;
          })
        )
        .attr("class", "bid-quan-area-level" + i + " " + uniqueLineClass)
        .attr("stroke-width", lineStrokeWidth)
        .attr(
          "d",
          getArea(function(d) {
            return y(d[API_KEYS_BID_ASK_QUANTITY["l" + i + "Bid"]]);
          })
        );

      svg
        .append("path")
        .datum(
          params.data.filter(function(obj) {
            return obj[API_KEYS_BID_ASK_QUANTITY["l" + i + "Ask"]] ? true : false;
          })
        )
        .attr("class", "ask-quan-area-level" + i + " " + uniqueLineClass)
        .attr("stroke-width", lineStrokeWidth)
        .attr(
          "d",
          getArea(function(d) {
            return y(d[API_KEYS_BID_ASK_QUANTITY["l" + i + "Ask"]]);
          })
        );
    }

    // ==================================================
    // MID PRICE LINE
    // ==================================================

    if (showMidPriceLineLayer) {
      //function to draw Mid Price line.
      let midPriceLine = d3
        .line()
        .x(function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset));
        })
        .y(function(d) {
          return dualY(d[API_KEYS_BID_ASK_QUANTITY.midPrice]);
        })
        .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));

      //Drawing Mid-price Line.
      svg
        .append("path")
        .datum(
          params.data.filter(function(obj) {
            return obj[API_KEYS_BID_ASK_QUANTITY.midPrice] ? true : false;
          })
        )
        .attr("class", "line mid-price-line " + uniqueLineClass)
        .attr("d", midPriceLine);
    }

    // ==================================================
    // FOCUS LINES
    // ==================================================

    var focus = svg.append("g").style("display", "none");
    //Creating X axis focus line

    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineX");

    //Creating top of the book ask focus line

    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYAskTopOfTheBook");

    //Creating top of the book bid focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYBidTopOfTheBook");
    //Create Top of the book Ask focus circle for point highlighting

    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleAskTopOfTheBook", "focus-point-ask-area-mirror");

    //Create Top of the book Bid focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleBidTopOfTheBook", "focus-point-bid-area-mirror");

    var bisectDate = d3.bisector(function(d) {
      return d[API_KEYS_BID_ASK_QUANTITY.timestamp];
    }).left;

    //drawing RECT in SVG to track mouse position on it
    svg
      .append("rect")
      .attr("class", "overlay")
      .attr("width", width)
      .attr("height", height)
      .on("mouseover", function() {
        focus.style("display", null);
      })
      .on("mouseout", function() {
        tooltip.style("display", "none");
        yAxisAskTooltip.style("display", "none");
        yAxisBidTooltip.style("display", "none");
        focus.style("display", "none");
      })
      .on("mousemove", function() {
        //getting current mouse position coordinates.
        var mouse = d3.mouse(this);
        var mouseDate = x.invert(mouse[0]);
        var i = bisectDate(params.data, moment(mouseDate).unix()); // returns the index to the current data item
        i = i >= params.data.length ? params.data.length - 1 : i;

        var d0 = params.data[i - 1]; //previous point
        var d1 = params.data[i]; //current point

        // Used to calculate difference between current data points and previous data points
        let previousDataObject = undefined;

        // work out which date value is closest to the mouse
        if (d0 !== undefined) {
          if (
            moment(mouseDate).unix() - d0[API_KEYS_BID_ASK_QUANTITY.timestamp] >
            d1[API_KEYS_BID_ASK_QUANTITY.timestamp] - moment(mouseDate).unix()
          ) {
            var d = d1;
            previousDataObject = d0;
          } else {
            var d = d0;
            previousDataObject = params.data[i - 2];
          }
          //var d = moment(mouseDate).unix() - d0[API_KEYS_BID_ASK_QUANTITY.timestamp] > d1[API_KEYS_BID_ASK_QUANTITY.timestamp] - moment(mouseDate).unix() ? d1 : d0;
        } else {
          var d = d1;
        }

        var hoveredXValue = x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)); //getting x value of current mouse position

        // ==================================================
        // DRAW COMMON TOOLTIP
        // ==================================================

        let tooltipContent =
          "<tr><td colspan=4>" +
          datetimeManager.getWholeDateTime(
            d[API_KEYS_BID_ASK_QUANTITY.timestamp],
            params.zoneOffset,
            commonRenderer.getXAxisDateFormat(params.granularityCode)
          ) +
          " " +
          params.zoneName +
          " (" +
          params.zoneTiming +
          ")" +
          "</td></tr>";

        tooltipContent += "<tr><td>Contract:</td><td colspan=3>" + d[API_KEYS_BID_ASK_QUANTITY.contract] + "</td></tr>";

        if (showMidPriceLineLayer) {
          tooltipContent +=
            "<tr><td>MidPrice:</td><td colspan=3>" +
            mathManager.toUSANumberFormat(d[API_KEYS_BID_ASK_QUANTITY.midPrice]) +
            " " +
            commonRenderer.getPercentageChange(
              previousDataObject ? previousDataObject[API_KEYS_BID_ASK_QUANTITY.midPrice] : undefined,
              d[API_KEYS_BID_ASK_QUANTITY.midPrice],
              params.granularityCode
            ) +
            "</td></tr>";
        }

        let howManyLevels = 1;
        if (showTop3LevelsLayer) howManyLevels = 3;
        else if (showTop5LevelsLayer) howManyLevels = 5;
        else if (showTop10LevelsLayer) howManyLevels = 10;

        for (let levelIndex = 1; levelIndex <= howManyLevels; levelIndex++) {
          let bidVal = Math.abs(d[API_KEYS_BID_ASK_QUANTITY["l" + levelIndex + "Bid"]]);
          let askVal = d[API_KEYS_BID_ASK_QUANTITY["l" + levelIndex + "Ask"]];
          let previousBidVal = previousDataObject ? Math.abs(previousDataObject[API_KEYS_BID_ASK_QUANTITY["l" + levelIndex + "Bid"]]) : undefined;
          let previousAskVal = previousDataObject ? previousDataObject[API_KEYS_BID_ASK_QUANTITY["l" + levelIndex + "Ask"]] : undefined;

          tooltipContent +=
            "<tr><td>" +
            (levelIndex === 1 ? "Top Of The Book" : "Level " + levelIndex) +
            ":</td><td>" +
            "B: " +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(bidVal, params.primaryChart)) +
            " " +
            commonRenderer.getPercentageChange(previousBidVal, bidVal, params.granularityCode) +
            "</td><td>" +
            "A: " +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(askVal, params.primaryChart)) +
            " " +
            commonRenderer.getPercentageChange(previousAskVal, askVal, params.granularityCode) +
            "</td><td>(Avg.: " +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType((bidVal + askVal) / 2, params.primaryChart)) +
            ")</td></tr>";
        }

        //mouse[0] is the x co-ordinate of current mouse position.
        commonRenderer.positionChartDataTooltip(width / 2, mouse[0], tooltip, tooltipContent, margin);

        // ==================================================
        // DRAW X AXIS (VERTICAL) FOCUS LINE.
        // ==================================================
        //drawing X line (vertical line)
        commonRenderer.positionXAxisFocusLine(focus, params.uniqueKey, "focusLineX", hoveredXValue, height);

        // =====================================================================================================
        // DRAW TOP OF THE BOOK ASK FOCUS LINE & TOOLTIP ON IT AND DRAW TOP OF THE BOOK ASK FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_BID_ASK_QUANTITY.l1Ask]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYAskTopOfTheBook",
            x(xDomain[0]),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Ask]),
            x(xDomain[1]),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Ask])
          );

          commonRenderer.positionChartTooltipAxis(yAxisAskTooltip, d[API_KEYS_BID_ASK_QUANTITY.l1Ask], y(d[API_KEYS_BID_ASK_QUANTITY.l1Ask]), params.primaryChart);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleAskTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Ask])
          );
        } else {
          focus.select("#focusLineYAskTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisAskTooltip.style("display", "none");
          focus.select("#focusCircleAskTopOfTheBook" + params.uniqueKey).style("display", "none");
        }

        // =====================================================================================================
        // DRAW TOP OF THE BOOK BID FOCUS LINE & TOOLTIP ON IT AND DRAW TOP OF THE BOOK BID FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_BID_ASK_QUANTITY.l1Bid]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYBidTopOfTheBook",
            x(xDomain[0]),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Bid]),
            x(xDomain[1]),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Bid])
          );

          commonRenderer.positionChartTooltipAxis(yAxisBidTooltip, -d[API_KEYS_BID_ASK_QUANTITY.l1Bid], y(d[API_KEYS_BID_ASK_QUANTITY.l1Bid]), params.primaryChart);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleBidTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)),
            y(d[API_KEYS_BID_ASK_QUANTITY.l1Bid])
          );
        } else {
          focus.select("#focusLineYBidTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisBidTooltip.style("display", "none");
          focus.select("#focusCircleBidTopOfTheBook" + params.uniqueKey).style("display", "none");
        }
      });
  }; //End of Render function.
  return { render };
})();

export default service;



// WEBPACK FOOTER //
// ./src/services/renderers/bid-ask-quantity-area-mirrored.js